import { ResidualsOutlookResponse } from './../models/dashboardModels';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AverageTicketVolume,
	BoardedMerchantCounts,
	IAverageTicketVolume,
	IBoardedMerchantCounts,
	IMerchantDashboardData,
	IPortfolioGrowth,
	MerchantDashboardData,
	PortfolioGrowth,
	ResidualTrends,
	ClosedMerchantCount,
	TopSalesEntities
} from '../models/EvoSalesPortalModel';
import { CacheService } from './cache.service';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { DashboardCardsList, DashboardOrder, DailyResidualsResponse } from '../models/dashboardModels';

@Injectable()
export class DashboardService {
	controller = 'api/dashboard/';

	constructor(private http: HttpClient, private cacheService: CacheService) { }

	public getAverageTicketVolume(
		iso?: number
	): Observable<Array<AverageTicketVolume>> {
		const url = this.controller + 'GetAverageTicketVolume';
		let parms: HttpParams;
		if (iso) {
			parms = new HttpParams().set('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<Array<AverageTicketVolume>>(url, {
				params: parms
			})
		);
		return observable;
	}

	public getTopPerformers(
		seid?: number
	): Observable<Array<TopSalesEntities>> {
		let url = this.controller + 'GetTopPerformers';
		if (seid) { url = url + '/' + seid.toString(); }
		const observable = this.cacheService.get(
			url,
			this.http.get<Array<TopSalesEntities>>(url, {})
		);
		return observable;
	}

	// Gets merchant application counts for the dashboard
	public getMerchantAppCounts(includeSubAgents: Boolean = true) {
		let url = this.controller + 'GetMerchantAppCounts/';
		if (includeSubAgents) {
			url = url + '0';
		} else {
			url = url + '1';
		}
		return this.http.get(url);
	}

	public getBoardedMerchantCounts(
		months: number,
		iso?: number
	): Observable<Array<IBoardedMerchantCounts>> {
		const url = this.controller + 'GetBoardedMerchantCounts';
		const parms = new HttpParams().set('mb', months.toString());
		if (iso) {
			parms.append('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<Array<IBoardedMerchantCounts>>(url, {
				params: parms
			})
		);
		return observable;
	}

	public getPortfolioGrowth(
		iso?: number
	): Observable<Array<PortfolioGrowth>> {
		const url = this.controller + 'GetPortfolioGrowth';
		let parms: HttpParams;
		if (iso) {
			parms = new HttpParams().set('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<Array<PortfolioGrowth>>(url, {
				params: parms
			})
		);
		return observable;
	}

	public getResidualTrends(
		iso?: number
	): Observable<Array<ResidualTrends>> {
		const url = this.controller + 'GetResidualTrends';
		let parms: HttpParams;
		if (iso) {
			parms = new HttpParams().set('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<Array<ResidualTrends>>(url, {
				params: parms
			})
		);
		return observable;
	}

	public GetClosedMerchants(
		iso?: number
	): Observable<Array<ClosedMerchantCount>> {
		const url = this.controller + 'GetClosedMerchants';
		let parms: HttpParams;
		if (iso) {
			parms = new HttpParams().set('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<Array<ClosedMerchantCount>>(url, {
				params: parms
			})
		);
		return observable;
	}

	public GetResidualOutlook(
		seid?: number
	): Observable<Array<ResidualsOutlookResponse>> {
		let url = this.controller + 'GetResidualOutlook';
		if (seid) { url = url + '/' + seid.toString(); }
		const observable = this.cacheService.get(
			url,
			this.http.get<Array<ResidualsOutlookResponse>>(url, {})
		);
		return observable;
	}

	public GetActiveResiduals(
		seid?: number
	): Observable<Array<DailyResidualsResponse>> {
		let url = this.controller + 'GetActiveResiduals';
		if (seid) { url = url + '/' + seid.toString(); }
		const observable = this.cacheService.get(
			url,
			this.http.get<Array<DailyResidualsResponse>>(url, {})
		);
		return observable;
	}

	public GetProcessingResults(
		seid?: number
	): Observable<Array<any>> {
		let url = this.controller + 'GetProcessingResults';
		if (seid) { url = url + '/' + seid.toString(); }
		const observable = this.cacheService.get(
			url,
			this.http.get<Array<any>>(url, {})
		);
		return observable;
	}

	public getAllDashboardData(
		months: number,
		iso?: number
	): Observable<MerchantDashboardData> {
		const url = this.controller + 'GetAllDashboardData/';
		const parms = new HttpParams().set('mb', months.toString());
		if (iso) {
			parms.append('iso', iso.toString());
		}
		const observable = this.cacheService.get(
			url + parms,
			this.http.get<IMerchantDashboardData>(url, {
				params: parms
			})
		);

		const result = from(observable).pipe(map(
			(src: IMerchantDashboardData) => {
				const dst: MerchantDashboardData = new MerchantDashboardData();

				src.AvgTicketVolume.forEach((item: IAverageTicketVolume) => {
					dst.AvgTicketVolume.push(new AverageTicketVolume(item));
				});
				src.BoardedMerchCounts.forEach(
					(item: IBoardedMerchantCounts) => {
						dst.BoardedMerchCounts.push(
							new BoardedMerchantCounts(item)
						);
					}
				);
				src.PortGrowth.forEach((item: IPortfolioGrowth) => {
					dst.PortGrowth.push(new PortfolioGrowth(item));
				});

				return dst;
			}
		));

		return result;
	}

	public getDashboardItems(
		sid: string = 'ESP',
		refreshCache: boolean = false
	): Observable<Array<DashboardCardsList>> {
		const url = 'api/Auth/GetDashboardItems/' + sid;
		let observable;
		observable = this.cacheService.get(
			url,
			this.http.get<Array<DashboardCardsList>>(url),
			null,
			refreshCache
		);
		return observable;
	}

	public UpdateDashboardItem(toUpdate: DashboardOrder) {
		if (!toUpdate.CardID) { throw new Error('CardID must be defined'); }
		if (!toUpdate.DisplayOrder) { throw new Error('DisplayOrder must be defined'); }
		if (toUpdate.Hidden === undefined) { throw new Error('Hidden must be defined'); }
		if (toUpdate.SystemID === undefined || toUpdate.SystemID === '') { toUpdate.SystemID = 'ESP'; }
		const url = 'api/auth/UpdateDashboardItem';
		return this.http.post(url, toUpdate);
	}

} // class
